<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { omit } from 'lodash-es';
import { useTableFilter, useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { usePermissions } from '@/composables/usePermissions';
import { useApi } from '@/api';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { useLodash } from '@/setup/lodash';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useTypedStore } from '@/store';
import { useDictList } from '@/composables/useDictList';
import type { UiTableNewField, UiTableNewFilter, UiTableNewSortEmit } from '@/types/table';
import type { SettingsPageQuery } from '@/types/core';
import type { GetDomainsManagementDnsHostingAccountsResponse, GetDomainsManagementDnsHostingAccountsData } from '@/types/openapi-ts';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
//#endregion

//#region types
type DnsHostingAccountsQuery = SettingsPageQuery<GetDomainsManagementDnsHostingAccountsData['query']>;
//#endregion

//#region const
const LS_PATH = 'DnsHostingAccounts';

const sizes = {
  1080: 20,
  1440: 30,
};

const canBeUndefined = [
  'auto_dns_management',
];

const fields: UiTableNewField<GetDomainsManagementDnsHostingAccountsResponse['payload'][number]>[] = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    align: 'left',
    width: '60',
  },
  {
    name: 'name',
    label: 'account_name',
    sortable: 'custom',
    minWidth: '100',
  },
  {
    name: 'dns_hosting_name',
    sortable: 'custom',
    computedValue: (row) => row.dns_hosting_name || '—',
  },
  {
    name: 'platform_service_domains_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'auto_dns_management',
    sortable: 'custom',
    computedValue: (row) => (row.auto_dns_management ? t('domainManagement.popup.form.auto_dns_management.auto') : t('domainManagement.popup.form.auto_dns_management.manual')),
  },
  {
    name: 'created_at',
    sortable: 'custom',
    width: '120',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const updateUrl = useUpdateUrl();
const { t } = useI18n();
const _ = useLodash();
const { hasPermission } = usePermissions();
const popupManager = usePopupManager();
const store = useTypedStore();
const { allAutoManualListBoolean } = useDictList();
const { searchQuery } = useTableSearch();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
const { filterParams, setFilter } = useTableFilter({
  lsPath: LS_PATH,
});
//#endregion

//#region state
const tableData = ref<GetDomainsManagementDnsHostingAccountsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
//#endregion

//#region computed
const dnsHostingProviders = computed(() => store.getters['misc/dnsHostingProviders']);

const canEditDnsHosting = computed(() => {
  return hasPermission('dns_hosting_accounts_edit');
});

const filtersList = computed(() => {
  return [
    {
      id: 'auto_dns_management',
      name: 'autoDnsManagement',
      type: 'radio',
      params: {
        options: allAutoManualListBoolean.value,
      },
    },
    {
      id: 'dns_hosting_id',
      name: 'dnsHostingId',
      type: 'select',
      params: {
        list: dnsHostingProviders.value,
      },
    },
  ];
});
//#endregion

//#region methods
function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);
    setFilter(_urlData);
    delete _urlData.page;
    delete _urlData.limit;

    _.merge(optionsSort.value, _urlData);
  }
  nextTick(() => {
    fetchData();
  });
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    search: searchQuery.value,
    offset: limit.value * page.value - limit.value,
  } as DnsHostingAccountsQuery;
}

function _completedFilterUrl() {
  const _dataFilters: DnsHostingAccountsQuery = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  _dataFilters.filters = JSON.stringify(filterParams.value);
  if(Object.keys(filterParams.value).length <= 0) {
    delete _dataFilters.filters;
  }
  updateUrl.updateGetParams(_dataFilters);
}

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();
  options = {
    ...options,
    ...filterParams.value,
  };

  try {
    const { data: { payload, misc } } = await api.getDNSHostingAccounts(options);

    count.value = misc!.count;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function handleRefresh () {
  page.value = 1;
  fetchData();
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};
function handleDelete(row: GetDomainsManagementDnsHostingAccountsResponse['payload'][number]) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('domainManagement.buttons.delete'),
      message: t('domainManagement.messages.delete', { name: row.name}),
      bodyIcon: 'trash-alt',
      confirmIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('domainManagement.buttons.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteDNSHostingAccount(row.id);
          await fetchData();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
}

async function openDnsHostingAccountPopup(id = null) {
  popupManager.dnsHostingAccountPopup.open({
    data: {
      dnsId: id,
    },
    listeners: {
      success: () => {
        fetchData(true);
      },
    },
  });
}

function getDnsHostingAccountFilter(data: UiTableNewFilter['filter']) {
  filterParams.value = data;
  handleRefresh();
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="dns-hosting-accounts">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('domainManagement.pageTitle.dnsHostingAccounts') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
        <DefaultFilter
          :model-value="filterParams"
          :filters-list="filtersList"
          :can-be-undefined="canBeUndefined"
          @submit="getDnsHostingAccountFilter"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="canEditDnsHosting"
          filled
          icon="plus"
          @click="openDnsHostingAccountPopup()"
        >
          {{ $t('domainManagement.buttons.add_dns_hosting_account') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="domainManagement"
      @sort="handleSort"
    >
      <template
        v-if="canEditDnsHosting"
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="pen"
              :tooltip="$t('domainManagement.buttons.edit')"
              @click="openDnsHostingAccountPopup(scope.row.id)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('domainManagement.buttons.delete')"
              :disabled="scope.row.platform_service_domains_count > 0"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
